import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

// Field definitions for pharmacyhaloDB.LocalWarnings
const GROUPS = [
  {
    label: "Quda",
    color: "#0d9488",
    fields: [
      { key: "Quda",         label: "Warning message",  helper: "Banner shown in Quda for this pharmacy only.", multiline: true },
      { key: "QudaLinkText", label: "Link button text",  helper: "Text on the optional action button (leave blank to hide)." },
      { key: "QudaLinkURL",  label: "Link URL",          helper: "URL opened when the Quda banner action button is clicked." },
    ],
  },
  {
    label: "HaloText",
    color: "#2563eb",
    fields: [
      { key: "HaloText", label: "Warning message", helper: "Banner shown in HaloText for this pharmacy only.", multiline: true },
    ],
  },
  {
    label: "MediDrop",
    color: "#f97316",
    fields: [
      { key: "MediDrop", label: "Warning message", helper: "Banner shown in MediDrop for this pharmacy only.", multiline: true },
    ],
  },
  {
    label: "DocLink",
    color: "#7c3aed",
    fields: [
      { key: "DocLink", label: "Warning message", helper: "Banner shown in DocLink for this pharmacy only.", multiline: true },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields.map((f) => f.key));

const empty = () => Object.fromEntries(ALL_FIELDS.map((k) => [k, ""]));

/**
 * Returns an array of app labels that have an active (non-empty) local warning.
 */
export function getActiveWarningLabels(warnings) {
  if (!warnings) return [];
  const active = [];
  if (warnings.Quda)     active.push("Quda");
  if (warnings.HaloText) active.push("HaloText");
  if (warnings.MediDrop) active.push("MediDrop");
  if (warnings.DocLink)  active.push("DocLink");
  return active;
}

const LocalWarningsModal = ({ isOpen, onClose, chemistID, chemistName, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [original, setOriginal] = useState(empty());
  const [values, setValues]     = useState(empty());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetch = async () => {
    if (!chemistID) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.LOCAL_WARNINGS, "GET", null, { ChemistID: chemistID });
      const w = res?.warnings ?? {};
      const next = empty();
      ALL_FIELDS.forEach((k) => { next[k] = w[k] ?? ""; });
      setOriginal(next);
      setValues(next);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load local warnings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetch();
    } else {
      setError(null);
      setOriginal(empty());
      setValues(empty());
    }
  }, [isOpen, chemistID]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key) => (e) => setValues((p) => ({ ...p, [key]: e.target.value }));
  const handleClear  = (key) => setValues((p) => ({ ...p, [key]: "" }));

  const isDirty = ALL_FIELDS.some((k) => (values[k] || "") !== (original[k] || ""));

  const handleSave = async () => {
    const changed = {};
    ALL_FIELDS.forEach((k) => {
      if ((values[k] || "") !== (original[k] || "")) {
        changed[k] = values[k] || "";
      }
    });
    if (Object.keys(changed).length === 0) return;

    setSaving(true);
    try {
      const res = await phappsRequest(API_PATHS.LOCAL_WARNINGS, "PUT", { ChemistID: chemistID, ...changed });
      const w = res?.warnings ?? {};
      const next = empty();
      ALL_FIELDS.forEach((k) => { next[k] = w[k] ?? ""; });
      setOriginal(next);
      setValues(next);
      if (onSaved) onSaved(res?.warnings ?? null);
      setSnackbar({ open: true, message: "Local warnings updated ✓", severity: "success" });
      onClose();
    } catch (e) {
      setSnackbar({ open: true, message: getAdminApiErrorMessage(e, "Failed to save local warnings"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fef3c715" }}>
              <WarningAmberOutlined sx={{ fontSize: 19, color: "#d97706" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}>
                Local Warnings
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {chemistName ? `Per-app banners for ${chemistName}` : "Per-app banners for this pharmacy"}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close" disabled={saving}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {GROUPS.map((group, gi) => (
                <Box key={group.label}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: group.color, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.25 }}>
                    {group.label}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {group.fields.map((field) => (
                      <Box key={field.key} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <TextField
                          label={field.label}
                          helperText={field.helper}
                          value={values[field.key] ?? ""}
                          onChange={handleChange(field.key)}
                          multiline={!!field.multiline}
                          minRows={field.multiline ? 2 : 1}
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                        <Tooltip title="Clear">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleClear(field.key)}
                              disabled={!values[field.key]}
                              sx={{ mt: 0.5 }}
                            >
                              <ClearOutlined sx={{ fontSize: 17 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                  {gi < GROUPS.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving || !!error || !isDirty}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: "#001a52" } }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LocalWarningsModal;
